import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'
import moment from 'moment'

import { type EmailStats, type EmailStatsCategory, type StatsPeriodDays } from '../types'

type EmailStatsQueryData = {
  stats: EmailStatsCategory[]
}

type UseEmailStatsParams = {
  periodDays: StatsPeriodDays
  skip?: boolean
}

const EMAIL_STATS = gql`
  query EmailLogsStats($startDate: String!, $endDate: String!) {
    stats: mailingEmailLogsStats(start_date: $startDate, end_date: $endDate) {
      type
      email_count
      daily_counts {
        date
        email_count
      }
    }
  }
`

const getEmailCount = (category?: EmailStatsCategory) => {
  return Number(category?.email_count ?? 0)
}

const useEmailStats = ({ periodDays, skip = false }: UseEmailStatsParams) => {
  const startDate = moment()
    .subtract(periodDays - 1, 'days')
    .format('YYYY-MM-DD')
  const endDate = moment().format('YYYY-MM-DD')
  const { data, loading, error } = useQuery<EmailStatsQueryData>(EMAIL_STATS, {
    variables: {
      startDate,
      endDate
    },
    fetchPolicy: 'network-only',
    skip
  })

  const stats = useMemo<EmailStats>(() => {
    const categories = data?.stats ?? []
    const campaigns = categories.find(({ type }) => type === 'campaigns')
    const triggers = categories.find(({ type }) => type === 'triggers')
    const campaignsSentCount = getEmailCount(campaigns)
    const triggersSentCount = getEmailCount(triggers)
    const totalSentCount = campaignsSentCount + triggersSentCount
    const daysInPeriod = Math.max(moment(endDate).diff(moment(startDate), 'days') + 1, 1)

    return {
      campaignsSentCount,
      triggersSentCount,
      totalSentCount,
      averageSentPerDay: totalSentCount / daysInPeriod,
      campaignsDailyCounts: campaigns?.daily_counts ?? [],
      triggersDailyCounts: triggers?.daily_counts ?? []
    }
  }, [data, endDate, startDate])

  return {
    endDate,
    error,
    loading,
    startDate,
    stats
  }
}

export default useEmailStats
